/**
 * Redirect URL sanitizer — protects against open-redirect attacks.
 *
 * Rules (kept intentionally strict):
 *  - Only accept relative paths starting with a single "/" (e.g. "/produto/x").
 *  - REJECT protocol-relative URLs ("//evil.com/x"), absolute URLs
 *    ("https://evil.com"), and paths with control characters.
 *  - REJECT javascript:, data:, mailto:, tel: and other schemes.
 *  - Fallback for anything else is the site root "/".
 *
 * Use it everywhere before doing `nav(redirect)` or `<Link to={redirect}>`
 * so a manipulated `?redirect=` query string can never bounce a signed-in
 * user to an external phishing page.
 */
export function sanitizeRedirect(raw, fallback = "/") {
  if (raw === null || raw === undefined) return fallback;
  const s = String(raw).trim();
  if (!s) return fallback;
  // Reject control characters (newlines, tabs) — those can smuggle headers.
  if (/[\r\n\t\0]/.test(s)) return fallback;
  // Must start with "/" AND the 2nd char must not be "/" or "\\" (blocks
  // protocol-relative "//evil.com" and Windows-style "/\evil.com").
  if (s.length < 1 || s[0] !== "/") return fallback;
  if (s.length >= 2 && (s[1] === "/" || s[1] === "\\")) return fallback;
  // Reject any dangerous scheme even when path-embedded (defense in depth).
  if (/^\s*(javascript|data|vbscript|file|about):/i.test(s)) return fallback;
  return s;
}

/**
 * Build a `?redirect=` query string suffix from the current location.
 * Returns "" (empty) when the current path is "/" — no need to bounce back.
 */
export function buildRedirectQuery(location) {
  const path = `${location.pathname || "/"}${location.search || ""}${location.hash || ""}`;
  if (path === "/") return "";
  return `?redirect=${encodeURIComponent(path)}`;
}
